import { section } from '@/glow';
import { add, contains, listen, qsa, remove } from '@fluorescent/dom';
import { AnimateShoppable } from '@/lib/animation';

const selectors = {
  hotspotWrappers: '.shoppable-item',
  hotspots: '.shoppable-item__hotspot',
  productCard: '.shoppable-item__product-card',
  closeButtons: '[data-shoppable-item-close]',
};

const classes = {
  animating: 'shoppable-item--animating',
  unset: 'shoppable-item--position-unset',
  hidden: 'hidden',
  active: 'active'
};

section('shoppable', {
  onLoad() {
    this.productCards = qsa(selectors.productCard, this.container);
    this.hotspotContainers = qsa(selectors.hotspotWrappers, this.container);
    this.hotspots = qsa(selectors.hotspots, this.container);
    const closeButtons = qsa(selectors.closeButtons, this.container);

    // Self terminating mouseenter events (stop animation on first hover)
    this.hotspotEvents = this.hotspots.map(hotspot => {
      return {
        element: hotspot,
        event: listen(hotspot, 'mouseenter', e => {
          remove(e.currentTarget.parentNode, classes.animating);
          this.hotspotEvents.find(o => o.element === hotspot).event();
        }),
      };
    });

    // Hover delay timer
    this._hoverTimer = null;

    this.events = [
      listen(this.hotspots, 'click', e => this._hotspotClickHandler(e)),
      listen(document, 'click', e => this._clickOutsideHandler(e)),
      listen(closeButtons, 'click', () => this._closeAll()),
      listen(this.container, 'keydown', ({ keyCode }) => {
        if (keyCode === 27) this._closeAll();
      }),
    ];

    // Desktop hover: show card on mouseenter, hide on mouseleave
    this.hoverEvents = this.hotspotContainers.map(wrapper => {
      const enterHandler = listen(wrapper, 'mouseenter', () => {
        if (!window.matchMedia('(min-width: 45em)').matches) return;
        clearTimeout(this._hoverTimer);
        this._showCard(wrapper);
      });
      const leaveHandler = listen(wrapper, 'mouseleave', () => {
        if (!window.matchMedia('(min-width: 45em)').matches) return;
        this._hoverTimer = setTimeout(() => {
          this._hideCard(wrapper);
        }, 200);
      });
      return [enterHandler, leaveHandler];
    });

    this.AnimateShoppable = AnimateShoppable(this.container);
  },

  _showCard(wrapper) {
    const card = wrapper.querySelector(selectors.productCard);
    if (!card) return;
    const cardHeight = card.offsetHeight;
    const cardWidth = card.offsetWidth;
    this._closeAll();
    card.setAttribute('aria-hidden', false);
    card.style.setProperty('--card-height', cardHeight + 'px');
    card.style.setProperty('--card-width', cardWidth + 'px');
    remove(card, classes.hidden);
    add(wrapper, classes.active);
    remove(wrapper, classes.unset);

    // Overflow protection for text-only cards
    this._adjustCardOverflow(card);
  },

  _adjustCardOverflow(card) {
    if (!card.classList.contains('shoppable-item__product-card--text-only')) return;

    // Reset any previous adjustments
    card.style.removeProperty('margin-left');
    card.style.removeProperty('top');

    const rect = card.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const margin = 8;

    // Right overflow
    if (rect.right > viewportWidth - margin) {
      const overflowX = rect.right - (viewportWidth - margin);
      card.style.marginLeft = `-${overflowX}px`;
    }

    // Left overflow
    if (rect.left < margin) {
      const overflowX = margin - rect.left;
      card.style.marginLeft = `${overflowX}px`;
    }

    // Bottom overflow — move card above the hotspot instead
    if (rect.bottom > viewportHeight - margin) {
      card.style.top = `auto`;
      card.style.bottom = `calc(100% + 8px)`;
    }
  },

  _hideCard(wrapper) {
    const card = wrapper.querySelector(selectors.productCard);
    if (!card) return;
    card.setAttribute('aria-hidden', true);
    add(card, classes.hidden);
    remove(wrapper, classes.active);
  },

  _hotspotClickHandler(e) {
    const hotspot = e.currentTarget;
    const wrapper = e.currentTarget.parentNode;
    const card = e.currentTarget.nextElementSibling;
    if (!card) return;

    if (contains(card, 'hidden')) {
      const cardHeight = card.offsetHeight;
      const cardWidth = card.offsetWidth;
      this._closeAll();
      card.setAttribute('aria-hidden', false);
      card.style.setProperty('--card-height', cardHeight + 'px');
      card.style.setProperty('--card-width', cardWidth + 'px');
      remove(card, classes.hidden);
      add(wrapper, classes.active);
      remove(wrapper, classes.unset);
      add(this.container, classes.flyupActive);

      // Overflow protection for text-only cards
      this._adjustCardOverflow(card);

      // Offset flyup height and scroll hotspot into view
      if (!window.matchMedia('(min-width: 45em)').matches) {
        const hotspotOffsetMargin = 70;
        const hotspotOffsetTop = hotspot.getBoundingClientRect().top;
        const positionFromBottom =
          window.innerHeight - (hotspotOffsetTop + hotspotOffsetMargin);

        if (cardHeight > positionFromBottom) {
          const y = window.pageYOffset + cardHeight - positionFromBottom;
          window.scrollTo({
            top: y,
            behavior: 'smooth',
          });
        }
      }
    } else {
      card.setAttribute('aria-hidden', true);
      add(card, classes.hidden);
      remove(this.container, classes.flyupActive);
      remove(wrapper, classes.active);
    }
  },

  _clickOutsideHandler(e) {
    if (
      !e.target.closest(selectors.productCard) &&
      !contains(e.target, 'shoppable-item__hotspot')
    ) {
      this._closeAll();
    }
  },

  _closeAll() {
    this.productCards.forEach(card => {
      add(card, classes.hidden);
      card.setAttribute('aria-hidden', true);
    });
    this.hotspotContainers.forEach(spot => remove(spot, classes.active));
    remove(this.container, classes.flyupActive);
  },

  onUnload() {
    this.events.forEach(unsubscribe => unsubscribe());
    this.hoverEvents.forEach(pair => pair.forEach(unsub => unsub()));
    clearTimeout(this._hoverTimer);
    this.AnimateShoppable?.destroy();
  },
});
